import { useState } from 'react';
import { Activity, Mail, Database, CreditCard, RefreshCw, CheckCircle, XCircle, AlertTriangle, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TestResult {
  status: 'idle' | 'running' | 'success' | 'error' | 'warning';
  message?: string;
  details?: string;
  timestamp?: string;
}

interface SystemTests {
  resend: TestResult;
  database: TestResult;
  stripe: TestResult;
  edgeFunctions: TestResult;
}

export function SystemHealthCheck() {
  const [testEmail, setTestEmail] = useState('');
  const [tests, setTests] = useState<SystemTests>({
    resend: { status: 'idle' },
    database: { status: 'idle' },
    stripe: { status: 'idle' },
    edgeFunctions: { status: 'idle' },
  });

  const updateTest = (testName: keyof SystemTests, result: Partial<TestResult>) => {
    setTests(prev => ({
      ...prev,
      [testName]: { ...prev[testName], ...result, timestamp: new Date().toISOString() }
    }));
  };

  const testResend = async () => {
    if (!testEmail) {
      updateTest('resend', {
        status: 'error',
        message: 'Email requis',
        details: 'Veuillez entrer une adresse email de test'
      });
      return;
    }

    updateTest('resend', { status: 'running', message: 'Test en cours...' });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Session non valide');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-resend`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ testEmail }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur inconnue');
      }

      updateTest('resend', {
        status: 'success',
        message: 'Email envoyé avec succès',
        details: `Email ID: ${result.emailId} - Destinataire: ${result.recipient}`
      });
    } catch (error: any) {
      updateTest('resend', {
        status: 'error',
        message: 'Échec de l\'envoi',
        details: error.message
      });
    }
  };

  const testDatabase = async () => {
    updateTest('database', { status: 'running', message: 'Test en cours...' });

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id')
        .limit(1);

      if (error) throw error;

      updateTest('database', {
        status: 'success',
        message: 'Connexion réussie',
        details: 'La base de données répond correctement'
      });
    } catch (error: any) {
      updateTest('database', {
        status: 'error',
        message: 'Échec de connexion',
        details: error.message
      });
    }
  };

  const testStripe = async () => {
    updateTest('stripe', { status: 'running', message: 'Test en cours...' });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Session non valide');
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-stripe-products`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur inconnue');
      }

      const result = await response.json();

      updateTest('stripe', {
        status: 'success',
        message: 'API Stripe opérationnelle',
        details: `${result.data?.length || 0} produits trouvés`
      });
    } catch (error: any) {
      updateTest('stripe', {
        status: 'error',
        message: 'Échec de connexion Stripe',
        details: error.message
      });
    }
  };

  const testEdgeFunctions = async () => {
    updateTest('edgeFunctions', { status: 'running', message: 'Test en cours...' });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Session non valide');
      }

      const functions = [
        'automatic-backup',
        'generate-financial-report',
        'get-stripe-products',
      ];

      const results = await Promise.allSettled(
        functions.map(fn =>
          fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${fn}`, {
            method: 'HEAD',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
            },
          })
        )
      );

      const available = results.filter(r => r.status === 'fulfilled').length;
      const total = functions.length;

      if (available === total) {
        updateTest('edgeFunctions', {
          status: 'success',
          message: 'Toutes les fonctions sont accessibles',
          details: `${available}/${total} fonctions répondent`
        });
      } else if (available > 0) {
        updateTest('edgeFunctions', {
          status: 'warning',
          message: 'Certaines fonctions ne répondent pas',
          details: `${available}/${total} fonctions répondent`
        });
      } else {
        updateTest('edgeFunctions', {
          status: 'error',
          message: 'Aucune fonction ne répond',
          details: `0/${total} fonctions accessibles`
        });
      }
    } catch (error: any) {
      updateTest('edgeFunctions', {
        status: 'error',
        message: 'Échec du test',
        details: error.message
      });
    }
  };

  const runAllTests = async () => {
    await testDatabase();
    await testStripe();
    await testEdgeFunctions();
    if (testEmail) {
      await testResend();
    }
  };

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'running':
        return <Loader className="animate-spin text-blue-500" size={24} />;
      case 'success':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'error':
        return <XCircle className="text-red-500" size={24} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={24} />;
      default:
        return <Activity className="text-gray-400" size={24} />;
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'running':
        return 'border-blue-200 bg-blue-50';
      case 'success':
        return 'border-green-200 bg-green-50';
      case 'error':
        return 'border-red-200 bg-red-50';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-gray-200 bg-white';
    }
  };

  const testItems = [
    {
      key: 'database' as keyof SystemTests,
      icon: Database,
      title: 'Base de données',
      description: 'Vérifie la connexion à Supabase',
      action: testDatabase,
    },
    {
      key: 'stripe' as keyof SystemTests,
      icon: CreditCard,
      title: 'Stripe API',
      description: 'Vérifie la connexion à Stripe',
      action: testStripe,
    },
    {
      key: 'edgeFunctions' as keyof SystemTests,
      icon: Activity,
      title: 'Edge Functions',
      description: 'Vérifie l\'accessibilité des fonctions',
      action: testEdgeFunctions,
    },
    {
      key: 'resend' as keyof SystemTests,
      icon: Mail,
      title: 'Resend (Email)',
      description: 'Envoie un email de test',
      action: testResend,
      requiresEmail: true,
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity size={28} />
            Diagnostic Système
          </h2>
          <p className="text-gray-600 mt-1">
            Testez les fonctions critiques de l'application
          </p>
        </div>
        <button
          onClick={runAllTests}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw size={18} />
          Tout tester
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {testItems.map(item => {
          const Icon = item.icon;
          const test = tests[item.key];

          return (
            <div
              key={item.key}
              className={`border-2 rounded-lg p-5 transition-all ${getStatusColor(test.status)}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Icon size={24} className="text-gray-700" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
                {getStatusIcon(test.status)}
              </div>

              {item.requiresEmail && (
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Email de test"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}

              <button
                onClick={item.action}
                disabled={test.status === 'running' || (item.requiresEmail && !testEmail)}
                className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {test.status === 'running' ? 'Test en cours...' : 'Tester'}
              </button>

              {test.message && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-900">{test.message}</p>
                  {test.details && (
                    <p className="text-xs text-gray-600 mt-1">{test.details}</p>
                  )}
                  {test.timestamp && (
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(test.timestamp).toLocaleString('fr-FR')}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <AlertTriangle size={18} className="text-yellow-600" />
          Informations importantes
        </h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Le test Resend nécessite que la variable RESEND_API_KEY soit configurée dans Supabase</li>
          <li>• Le test Stripe nécessite que STRIPE_SECRET_KEY soit configuré</li>
          <li>• Les tests Edge Functions vérifient uniquement l'accessibilité, pas l'exécution complète</li>
          <li>• Pour un email de test Resend, utilisez une vraie adresse email que vous possédez</li>
        </ul>
      </div>
    </div>
  );
}
